// Report Builder Types — mirrors backend Go types (snake_case wire format)

export type DataType = 'string' | 'integer' | 'decimal' | 'boolean' | 'date' | 'timestamp' | 'uuid';
export type AggregationType = 'count' | 'count_distinct' | 'sum' | 'avg' | 'min' | 'max';
export type FilterOperator = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'not_in' | 'like' | 'is_null' | 'is_not_null' | 'between' | 'relative_date';
export type DisplayFormat = '' | 'percentage' | 'currency' | 'date_short' | 'datetime' | 'decimal_2';
export type JoinType = 'INNER' | 'LEFT';
export type ScopingType = 'client_admin' | 'global_hr' | 'scoped_hr' | 'executive' | 'manager' | 'self';

export interface DataModelColumn {
  key: string;
  label: string;
  data_type: DataType;
  source_expression: string;
  is_dimension: boolean;
  is_measure: boolean;
  aggregation_types?: AggregationType[];
  allowed_operators: FilterOperator[];
  filter_input?: '' | 'select' | 'multi';
  filter_options?: string[];
  display_format?: DisplayFormat;
  filterable: boolean;
  sortable: boolean;
  groupable: boolean;
  default_visible: boolean;
  is_custom_field?: boolean;
  sort_order: number;
}

export interface DataModelJoin {
  target_model_key: string;
  join_type: JoinType;
  join_condition: string;
}

export interface ScopingConfig {
  account_field: string;
  user_scope_field?: string;
  manager_field?: string;
}

export interface DataModel {
  key: string;
  name: string;
  description: string;
  module: string;
  base_table: string;
  table_alias: string;
  columns: Record<string, DataModelColumn>;
  column_order: string[];
  joins?: DataModelJoin[];
  scoping: ScopingConfig;
}

export type TimeGranularity = 'year' | 'quarter' | 'month' | 'week' | 'day';

export interface TimeGroupingSpec {
  column: string;
  granularity: TimeGranularity;
}

export interface MeasureSpec {
  column: string;
  aggregation: AggregationType;
  alias: string;
}

export interface FilterSpec {
  column: string;
  operator: FilterOperator;
  value: unknown;
}

export interface SortSpec {
  column: string;
  direction: 'asc' | 'desc';
}

export interface HavingFilterSpec {
  measure_alias: string;
  operator: FilterOperator;
  value: unknown;
}

// --- Calculated Fields ---

export type CalculatedFieldType = 'case_bucket' | 'date_diff' | 'ratio';

export interface CalculatedFieldSpec {
  key: string;
  label: string;
  field_type: CalculatedFieldType;
  config: CaseBucketConfig | DateDiffConfig | RatioConfig;
  as_dimension: boolean;
}

export interface CaseBucketConfig {
  source_column: string;
  buckets: BucketDef[];
  else_label: string;
}

export interface BucketDef {
  label: string;
  min_value?: number;
  max_value?: number;
  equals?: string;
}

export interface DateDiffConfig {
  start_column: string;
  end_column: string; // column key or "__now__"
  unit: 'year' | 'month' | 'day';
}

export interface RatioConfig {
  numerator: MeasureSpec;
  denominator: MeasureSpec;
}

// --- Window Functions ---

export type WindowFuncType = 'rank' | 'dense_rank' | 'row_number' | 'lag' | 'lead' | 'running_sum';

export interface WindowFunctionSpec {
  key: string;
  label: string;
  function: WindowFuncType;
  column: string;               // column/measure alias to operate on
  partition_by?: string[];      // column keys for PARTITION BY
  order_by: SortSpec[];         // columns for ORDER BY within window
  params?: Record<string, unknown>; // e.g. lag offset
}

export const WINDOW_FUNC_LABELS: Record<WindowFuncType, string> = {
  rank: 'Rank',
  dense_rank: 'Dense Rank',
  row_number: 'Row Number',
  lag: 'Lag (Previous)',
  lead: 'Lead (Next)',
  running_sum: 'Running Sum',
};

// --- Period Comparison ---

export type CompareType = 'yoy' | 'mom' | 'qoq' | 'custom';

export interface PeriodComparisonSpec {
  date_column: string;
  compare_type: CompareType;
  current_start?: string;  // YYYY-MM-DD (custom only)
  current_end?: string;
  previous_start?: string;
  previous_end?: string;
}

export const COMPARE_TYPE_LABELS: Record<CompareType, string> = {
  yoy: 'Year over Year',
  mom: 'Month over Month',
  qoq: 'Quarter over Quarter',
  custom: 'Custom Range',
};

export interface QuerySpec {
  model: string;
  join_models?: string[];
  columns: string[];
  measures?: MeasureSpec[];
  filters?: FilterSpec[];
  having_filters?: HavingFilterSpec[];
  calculated_fields?: CalculatedFieldSpec[];
  window_functions?: WindowFunctionSpec[];
  period_comparison?: PeriodComparisonSpec;
  sort?: SortSpec[];
  time_groupings?: TimeGroupingSpec[];
  limit?: number;
  offset?: number;
  scoping_type?: ScopingType;
}

export interface ColumnMeta {
  key: string;
  label: string;
  data_type: DataType;
  display_format?: DisplayFormat;
  is_dimension: boolean;
  is_measure: boolean;
  is_time_grouping?: boolean;
  is_calculated?: boolean;
  granularity?: TimeGranularity;
  sortable: boolean;
}

export interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface QueryStats {
  query_time_ms: number;
  row_count: number;
  sql?: string;
}

export interface QueryResult {
  data: Record<string, unknown>[];
  columns: ColumnMeta[];
  pagination: PaginationInfo | null;
  query_stats: QueryStats;
}

export interface ListModelsResponse {
  models: Array<{
    key: string;
    name: string;
    description: string;
    module: string;
    joinable_models: string[];
  }>;
}

export interface ValidateResponse { valid: boolean; error?: string; }
export interface PreviewSQLResponse { sql: string; }

// --- UI-only types ---

export interface FilterRow extends FilterSpec { id: string; }
export interface HavingFilterRow extends HavingFilterSpec { id: string; }
export interface SortRow extends SortSpec { id: string; }

// --- Visualization Types ---

export type ReportChartType = 'bar' | 'column' | 'line' | 'area' | 'pie' | 'donut' | 'radar' | 'h-bar' | 'stacked' | 'scatter';
export type ResultViewMode = 'table' | 'chart' | 'pivot';

export interface PivotConfig {
  rowField: string;
  columnField: string;
  valueField: string;
  showRowTotals: boolean;
  showColumnTotals: boolean;
}

export interface ChartFieldMapping {
  nameField: string;       // X-axis (category dimension)
  valueField: string;      // Y-axis (primary measure)
  value2Field?: string;    // Secondary Y-axis (scatter/dual-axis)
  seriesField?: string;    // Series/Color (secondary dimension for stacked/grouped)
}

export interface VisualizationConfig {
  chartType: ReportChartType;
  fieldMapping: ChartFieldMapping;
  showLegend: boolean;
  showDataLabels: boolean;
}

export interface SavedReport {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  querySpec: QuerySpec;
  selectedModelKeys: string[];
  selectedDimensions: string[];
  selectedMeasures: MeasureSpec[];
  timeGroupings: TimeGroupingSpec[];
  filters: FilterRow[];
  sorts: SortRow[];
  limit: number;
  visualization: VisualizationConfig | null;
  viewMode: ResultViewMode;
}

// --- Saved Report Config (API-backed) ---

export interface SavedReportConfig {
  id: string;
  account_id: string;
  created_by: string;
  name: string;
  description: string;
  query_spec: QuerySpec;
  visualization_config: VisualizationConfig | null;
  grid_config: Record<string, unknown> | null;
  model_keys: string[];
  builder_state: SavedBuilderState | null;
  created_at: string;
  updated_at: string;
  restricted?: boolean;
}

export interface SavedBuilderState {
  selectedDimensions: string[];
  selectedMeasures: MeasureSpec[];
  timeGroupings: TimeGroupingSpec[];
  filters: FilterRow[];
  havingFilters?: HavingFilterRow[];
  calculatedFields?: CalculatedFieldSpec[];
  windowFunctions?: WindowFunctionSpec[];
  periodComparison?: PeriodComparisonSpec;
  sorts: SortRow[];
  limit: number;
  viewMode: ResultViewMode;
  scopingType?: ScopingType;
}

export interface CreateSavedReportRequest {
  name: string;
  description: string;
  query_spec: QuerySpec;
  visualization_config?: VisualizationConfig | null;
  grid_config?: Record<string, unknown> | null;
  model_keys: string[];
  builder_state?: SavedBuilderState | null;
}

export interface UpdateSavedReportRequest {
  name?: string;
  description?: string;
  query_spec?: QuerySpec;
  visualization_config?: VisualizationConfig | null;
  grid_config?: Record<string, unknown> | null;
  model_keys?: string[];
  builder_state?: SavedBuilderState | null;
}

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  eq: 'equals', ne: 'not equals', gt: 'greater than', gte: 'greater or equal',
  lt: 'less than', lte: 'less or equal', in: 'in', not_in: 'not in',
  like: 'contains', is_null: 'is empty', is_not_null: 'is not empty', between: 'between',
  relative_date: 'relative date',
};

export const RELATIVE_DATE_PRESETS: { label: string; value: string }[] = [
  { label: 'Last 7 days', value: 'last_7_days' },
  { label: 'Last 30 days', value: 'last_30_days' },
  { label: 'Last 90 days', value: 'last_90_days' },
  { label: 'Last 365 days', value: 'last_365_days' },
  { label: 'Year to date', value: 'ytd' },
  { label: 'This quarter', value: 'this_quarter' },
  { label: 'This month', value: 'this_month' },
  { label: 'Last quarter', value: 'last_quarter' },
  { label: 'Last month', value: 'last_month' },
];

export const HAVING_OPERATORS: FilterOperator[] = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between'];

// --- Conditional Formatting ---

export type ConditionalFormatType = 'threshold' | 'data_bar' | 'color_scale';

export interface ConditionalFormatRule {
  id: string;
  column: string;            // column key to apply formatting
  type: ConditionalFormatType;
  config: ThresholdConfig | DataBarConfig | ColorScaleConfig;
}

export interface ThresholdCondition {
  operator: 'gt' | 'gte' | 'lt' | 'lte' | 'eq';
  value: number;
  color: string;       // text color
  bgColor: string;     // background color
}

export interface ThresholdConfig {
  conditions: ThresholdCondition[];
  defaultBgColor: string;
}

export interface DataBarConfig {
  color: string;
  maxValue?: number;   // if omitted, computed from data
}

export interface ColorScaleConfig {
  minColor: string;
  midColor?: string;
  maxColor: string;
}
